import {
  type AnalyticsCompare,
  type AnalyticsGranularity,
  type AnalyticsSection,
  analyticsCompareSchema,
  analyticsGranularitySchema,
  dimensionFor,
} from '#shared/types';
import { dateKey } from '#client/lib/dates';
import { parseEnum } from '#client/lib/url-params';

/**
 * Reading the analytics filters out of the query string, and writing the wire
 * query back out. Pure, so the clock is passed in rather than read.
 */

export type RangePreset = '7d' | '30d' | '90d' | 'qtd' | 'ytd' | 'custom';

const RANGE_PRESETS: readonly RangePreset[] = ['7d', '30d', '90d', 'qtd', 'ytd', 'custom'];

/** Thirty days: long enough for a weekly pattern, short enough to stay daily. */
export const DEFAULT_PRESET: RangePreset = '30d';

export function parsePreset(raw: string | null): RangePreset {
  return parseEnum(raw, RANGE_PRESETS, DEFAULT_PRESET);
}

/** A comma-separated id list, keeping only positive integers. */
export function parseIds(raw: string | null): number[] {
  return (raw ?? '')
    .split(',')
    .map((part) => Number(part.trim()))
    .filter((id) => Number.isInteger(id) && id > 0);
}

/** An id list as a query value, omitted when empty. */
export function joinIds(ids: readonly number[]): string | undefined {
  return ids.length > 0 ? ids.join(',') : undefined;
}

/**
 * The dates a preset resolves to, in the **browser's** local calendar.
 *
 * Local, not `toISOString()`: that converts to UTC first, so at 9 a.m. in
 * Karachi on the first of a month it would return the previous month's last
 * day. The same trap `useDateRange` documents for the finance screens.
 */
export function resolvePresetRange(
  preset: RangePreset,
  custom: { from: string | null; to: string | null },
  now: Date,
): { from: string; to: string } {
  const today = dateKey(now);

  if (preset === 'custom' && custom.from && custom.to) return { from: custom.from, to: custom.to };

  const back = (days: number) =>
    dateKey(new Date(now.getFullYear(), now.getMonth(), now.getDate() - days + 1));

  switch (preset) {
    case '7d':
      return { from: back(7), to: today };
    case '90d':
      return { from: back(90), to: today };
    case 'qtd': {
      const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
      return { from: dateKey(quarterStart), to: today };
    }
    case 'ytd':
      return { from: dateKey(new Date(now.getFullYear(), 0, 1)), to: today };
    default:
      return { from: back(30), to: today };
  }
}

export const PRESET_LABELS: Record<RangePreset, string> = {
  '7d': '7 days',
  '30d': '30 days',
  '90d': '90 days',
  qtd: 'Quarter',
  ytd: 'Year',
  custom: 'Custom',
};

export type IdParam = 'department_ids' | 'test_ids' | 'panel_ids' | 'user_ids';

export interface AnalyticsFilterValues {
  by: string;
  compare: AnalyticsCompare;
  granularity: AnalyticsGranularity | undefined;
  includeApproximate: boolean;
  departmentIds: number[];
  testIds: number[];
  panelIds: number[];
  userIds: number[];
}

export function readFilterValues(
  params: URLSearchParams,
  section: AnalyticsSection,
): AnalyticsFilterValues {
  const rawGranularity = params.get('granularity');

  return {
    by: dimensionFor(section, params.get('by') ?? undefined),
    compare: parseEnum(params.get('compare'), analyticsCompareSchema.options, 'none'),
    granularity: rawGranularity
      ? parseEnum(rawGranularity, analyticsGranularitySchema.options, 'day')
      : undefined,
    includeApproximate: params.get('approximate') === 'true',
    departmentIds: parseIds(params.get('department_ids')),
    testIds: parseIds(params.get('test_ids')),
    panelIds: parseIds(params.get('panel_ids')),
    userIds: parseIds(params.get('user_ids')),
  };
}

/** The wire query. Every key omitted when it is at its default, so links stay short. */
export function filterQuery(
  range: { from: string; to: string; by: string | undefined },
  values: AnalyticsFilterValues,
): Record<string, string | undefined> {
  return {
    ...range,
    compare: values.compare === 'none' ? undefined : values.compare,
    granularity: values.granularity,
    include_approximate: values.includeApproximate ? 'true' : undefined,
    department_ids: joinIds(values.departmentIds),
    test_ids: joinIds(values.testIds),
    panel_ids: joinIds(values.panelIds),
    user_ids: joinIds(values.userIds),
  };
}

/** True when any dimension filter is narrowing the data. */
export function hasDimensionFilter(values: AnalyticsFilterValues): boolean {
  return [values.departmentIds, values.testIds, values.panelIds, values.userIds].some(
    (ids) => ids.length > 0,
  );
}
